import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { FIRM, NAV_LINKS } from "../lib/constants";
import { Button } from "./ui/button";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/90 border-b border-[#E2E8F0]"
          : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="h-20 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="nav-logo-link"
            aria-label="Mizan Audit & Assurance LLP home"
          >
            <img
              src={FIRM.logo}
              alt="Mizan Audit & Assurance LLP"
              className="h-11 w-11 object-contain"
            />
            <div className="leading-tight hidden sm:block">
              <div className="font-display text-lg text-[#0B2E5C] tracking-tight">
                Mizan
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#2BA8B8] font-semibold">
                Audit & Assurance LLP
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm tracking-wide transition-colors ${
                    isActive
                      ? "text-[#0B2E5C] font-semibold"
                      : "text-[#475569] hover:text-[#0B2E5C]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={FIRM.phoneHref}
              data-testid="nav-phone-link"
              className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#0B2E5C] transition-colors"
            >
              <Phone className="h-4 w-4" strokeWidth={1.5} />
              <span>{FIRM.phone}</span>
            </a>
            <Link to="/contact" data-testid="nav-cta-contact">
              <Button className="rounded-sm bg-[#0B2E5C] hover:bg-[#082142] text-white px-5 h-10 text-sm tracking-wide">
                Request a Proposal
              </Button>
            </Link>
          </div>

          <button
            className="lg:hidden p-2 -mr-2 text-[#0B2E5C]"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-6 border-t border-[#E2E8F0] -mx-6 px-6 md:-mx-12 md:px-12 bg-white" data-testid="mobile-menu">
            <nav className="flex flex-col py-4">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `py-3 text-base border-b border-[#E2E8F0] ${
                      isActive ? "text-[#0B2E5C] font-semibold" : "text-[#475569]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
            <div className="flex flex-col gap-3 mt-4">
              <a
                href={FIRM.phoneHref}
                className="text-sm text-[#475569]"
                data-testid="mobile-phone-link"
              >
                <Phone className="inline h-4 w-4 mr-2" strokeWidth={1.5} />
                {FIRM.phone}
              </a>
              <Link to="/contact" data-testid="mobile-cta-contact">
                <Button className="w-full rounded-sm bg-[#0B2E5C] hover:bg-[#082142] text-white h-11">
                  Request a Proposal
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
